import { EFFECT_ACTIVE, isActive, isPaused, clearDirty, clearScheduled, clearActive } from './effect-flags'
import { nextSignalId } from './signal-id'
import { getRequestContext } from './request-context'
import { setV2EffectRunner, queueJob as v1QueueJob } from './lifecycle'
import type { ScheduleEffectFn } from './scheduler'

/** Pre-sized initial capacity for effects array. */
const INITIAL_EFFECTS_SIZE = 4

/**
 * SignalContainer — stable hidden class (V8 monomorphic).
 *
 * IMPORTANT: Always allocate via `makeSignalContainer()`. Never construct inline
 * with object literals that have different property sets, because that splits
 * the hidden class and breaks V8's inline cache.
 */
export interface SignalContainer<T = unknown> {
    readonly id: number
    value: T
    effects: EffectRecord[]
    flags: number
}

export interface EffectRecord {
    id: number
    fn: () => void
    deps: SignalContainer[]
    flags: number
    scheduler: (() => void) | undefined
}

let _nextEffectId = 1
export function nextEffectId(): number { return _nextEffectId++ }
/** @internal — test-only */
export function __resetEffectId(): void { _nextEffectId = 1 }

/** Factory — always produces same property order → same hidden class. */
export function makeSignalContainer<T>(initialValue: T): SignalContainer<T> {
    const id = nextSignalId()
    return { id, value: initialValue, effects: [], flags: 0 }
}

/**
 * SignalContainerV2 — stable-shape container returned from createSignalV2.
 *
 * All five data properties (type, _v2, _getter, _setter, _v2Container) are
 * assigned in constructor order so every instance shares the same V8 hidden
 * class. The `value` accessor lives on the prototype, so closures are
 * allocated once (on the prototype) rather than per signal — this replaces
 * the per-call `hybrid` object literal that use.ts previously allocated
 * after every createSignalV2() call.
 *
 * `this`-binding note: `get value()` resolves `_getter`/`_setter` via `this`.
 * Flexium's internal access patterns (`container.value`, `container._setter(x)`)
 * always preserve the correct receiver. Destructuring (`const { value } =
 * container`) would lose `this` and is not used internally.
 */
export class SignalContainerV2<T = unknown> {
    readonly type = 'signal' as const
    readonly _v2 = true as const
    readonly _getter: () => T
    readonly _setter: (newValue: T | ((prev: T) => T)) => void
    readonly _v2Container: SignalContainer<T>

    constructor(
        getter: () => T,
        setter: (newValue: T | ((prev: T) => T)) => void,
        container: SignalContainer<T>
    ) {
        this._getter = getter
        this._setter = setter
        this._v2Container = container
    }

    get value(): T {
        return this._getter()
    }

    set value(v: T) {
        this._setter(v)
    }
}

/** Factory — always produces same property order. */
export function makeEffectRecord(
    fn: () => void,
    scheduler?: () => void
): EffectRecord {
    return {
        id: nextEffectId(),
        fn,
        deps: [],
        flags: EFFECT_ACTIVE,
        scheduler: scheduler ?? undefined,
    }
}

export { INITIAL_EFFECTS_SIZE }

// ---------------------------------------------------------------------------
// Active-effect tracking (uses existing RequestContext.activeEffect slot —
// we store EffectRecord there during V2 effect execution)
// ---------------------------------------------------------------------------

/** Get the currently-running V2 effect (or undefined if none). */
export function getActiveEffectV2(): EffectRecord | undefined {
    // Re-use the existing per-request slot. During V2 execution the stored value
    // is an EffectRecord (which satisfies the ReactiveEffectLike duck-type for the slot).
    return getRequestContext().activeEffect as unknown as EffectRecord | undefined
}

// ---------------------------------------------------------------------------
// track — called inside a signal getter when an effect is running
// ---------------------------------------------------------------------------

/**
 * Record that the currently-active effect reads from `signal`.
 *
 * Complexity: O(n) scan of signal.effects[] to avoid duplicates.
 * For typical component renders, n < 20. A bitfield active-mask would reduce
 * this to O(1) but requires capping total signal count — deferred to Phase 4.
 *
 * V1 compatibility: if the active effect is a V1 ReactiveEffectLike (lacks
 * .flags), we still register it in signal.effects[] so trigger can dispatch
 * it via the V1 path.
 */
export function trackV2(signal: SignalContainer): void {
    const ctx = getRequestContext()
    const effect = ctx.activeEffect as unknown as EffectRecord | undefined
    if (!effect) return

    // Detect V1 effect — it has .active boolean, not .flags number
    const isV1 = typeof (effect as unknown as { active?: boolean }).active === 'boolean'

    if (isV1) {
        // V1 active effect — register the V1 record so triggerV2 can dispatch
        // it via the V1 queueJob path. Bypass V2 flag checks.
        const v1 = effect as unknown as { active: boolean }
        if (!v1.active) return
    } else {
        if (!isActive(effect.flags)) return
    }

    // Avoid duplicate registration
    const effs = signal.effects
    for (let i = 0; i < effs.length; i++) {
        if (effs[i] === effect) return
    }

    signal.effects.push(effect)
    if (!isV1) {
        effect.deps.push(signal)
    }
    // For V1 effects, we don't push into deps — V1 deps is Set<ReactiveEffect>[].
    // Trade-off: V1 effect cleanup won't unsubscribe from V2 signals automatically.
    // Phase D Task 22 will document this; full cleanup arrives when V1 is removed
    // in Task 26 (soak period).
}

// ---------------------------------------------------------------------------
// trigger — called inside a signal setter when value changes
// ---------------------------------------------------------------------------

/**
 * Notify all subscriber effects that `signal` changed.
 *
 * Effects are iterated via a snapshot copy so that re-entrant mutations are safe.
 *
 * V1 compatibility: V1 ReactiveEffectLike records are detected via `typeof
 * .active === 'boolean'` and dispatched through `queueJob` (V1 microtask
 * scheduler) — letting V1 components correctly re-run when they read a V2
 * signal.
 */
export function triggerV2(
    signal: SignalContainer,
    scheduleEffect: (e: EffectRecord) => void
): void {
    const effs = signal.effects
    if (effs.length === 0) return

    // Snapshot: safe against re-entrant modifications during iteration
    const snapshot = effs.slice()
    const activeEffect = getActiveEffectV2()

    for (let i = 0; i < snapshot.length; i++) {
        const effect = snapshot[i]
        if (effect === activeEffect) continue              // skip self-trigger

        const asV1 = effect as unknown as { active?: boolean; scheduler?: () => void; run?: () => void }
        if (typeof asV1.active === 'boolean') {
            // V1 record path
            if (!asV1.active) continue
            if (asV1.scheduler) {
                asV1.scheduler()
            } else {
                // Use V1 queueJob via the late-bound scheduler hook
                v1QueueJobHook(effect)
            }
            continue
        }

        // V2 record path
        if (!isActive(effect.flags)) continue              // stopped effects
        if (isPaused(effect.flags)) continue               // paused effects

        if (effect.scheduler) {
            effect.scheduler()
        } else {
            scheduleEffect(effect)
        }
    }
}

// Late-bound hook so we don't create a static circular dep on lifecycle.ts.
// reactive-v2.ts ← scheduler.ts ← lifecycle.ts already chains; we avoid going back.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let v1QueueJobHook: (effect: any) => void = () => {}
/** @internal — bound from this module's bottom block via lifecycle's queueJob. */
export function __setV1QueueJobHook(fn: (effect: unknown) => void): void {
    v1QueueJobHook = fn
}

// ---------------------------------------------------------------------------
// createSignalV2 — public V2 signal factory
// ---------------------------------------------------------------------------

/**
 * Create a V2 reactive signal.
 *
 * Returns a SignalContainerV2 instance — a stable-hidden-class object that
 * exposes `_getter`/`_setter` for fast direct access and `value` accessor pair
 * (defined on the prototype) for ergonomic read/write.
 *
 * Getter calls trackV2(); setter calls triggerV2(). No Proxy involved.
 */
export function createSignalV2<T>(
    initialValue: T,
    scheduleEffect: ScheduleEffectFn
): SignalContainerV2<T> {
    const container = makeSignalContainer(initialValue)

    function getter(): T {
        trackV2(container)
        return container.value
    }

    function setter(newValue: T | ((prev: T) => T)): void {
        const next = typeof newValue === 'function'
            ? (newValue as (prev: T) => T)(container.value)
            : newValue

        if (Object.is(next, container.value)) return   // no-op: same value

        container.value = next
        triggerV2(container, scheduleEffect)
    }

    return new SignalContainerV2(getter, setter, container)
}

/**
 * Test-only tuple adapter — preserves the pre-Phase-5 `[getter, setter, container]`
 * destructuring contract for the internal V2 regression suite (regression-v2.test.ts,
 * reactive-v2.test.ts). NOT exported from the package; consumers must use
 * createSignalV2() and read .[_getter,_setter,_v2Container] / .value.
 *
 * @internal — test-only
 */
export function __createSignalV2Tuple<T>(
    initialValue: T,
    scheduleEffect: ScheduleEffectFn
): [() => T, (newValue: T | ((prev: T) => T)) => void, SignalContainer<T>] {
    const sc = createSignalV2(initialValue, scheduleEffect)
    return [sc._getter, sc._setter, sc._v2Container]
}

// ---------------------------------------------------------------------------
// runEffectV2 / stopEffectV2 — effect execution + lifecycle
// ---------------------------------------------------------------------------

/**
 * Remove `effect` from all signals it is currently subscribed to,
 * then clear its deps list. Called before re-running to rebuild dep set.
 */
function cleanupEffect(effect: EffectRecord): void {
    const { deps } = effect
    for (let i = 0; i < deps.length; i++) {
        const signal = deps[i]
        const effs = signal.effects
        // Swap-remove for O(1) deletion (order doesn't matter for effect sets)
        const idx = effs.indexOf(effect)
        if (idx !== -1) {
            effs[idx] = effs[effs.length - 1]
            effs.pop()
        }
    }
    deps.length = 0
}

/**
 * Run `effect.fn()` with dependency tracking.
 * Restores previous active effect after execution (supports nested effects).
 */
export function runEffectV2(effect: EffectRecord): void {
    if (!isActive(effect.flags)) {
        effect.fn()   // stopped but called directly: run without tracking
        return
    }

    const ctx = getRequestContext()
    const parent = ctx.activeEffect

    try {
        cleanupEffect(effect)
        effect.flags = clearDirty(effect.flags)
        effect.flags = clearScheduled(effect.flags)
        ctx.activeEffect = effect as unknown as import('./lifecycle').ReactiveEffect
        effect.fn()
    } finally {
        ctx.activeEffect = parent
    }
}

/**
 * Stop an effect permanently: mark inactive, unsubscribe from all signals.
 */
export function stopEffectV2(effect: EffectRecord): void {
    if (!isActive(effect.flags)) return
    cleanupEffect(effect)
    effect.flags = clearActive(effect.flags)
}

// Register V2 dispatcher with lifecycle.ts so V1 sync() drains V2 effects too.
// This is a one-time bind at module-init; safe because lifecycle.ts cannot
// statically import this module (it imports types via `./lifecycle` here).
setV2EffectRunner((effect) => runEffectV2(effect as EffectRecord))

// Bind V1 queueJob so triggerV2 can dispatch V1 effects when they read V2 signals.
__setV1QueueJobHook((effect: unknown) => v1QueueJob(effect as import('./lifecycle').ReactiveEffect))

// ---------------------------------------------------------------------------
// createEffectV2 — public effect creator (analog to unsafeEffect)
// ---------------------------------------------------------------------------

import { getComponent, registerEffectWithComponent } from './hook'
import type { ReactiveEffectLike } from './lifecycle'

/**
 * Wrap a V2 EffectRecord as a ReactiveEffectLike duck-type for compatibility
 * with hook.ts registerEffectWithComponent / stopComponentEffects.
 */
function wrapAsReactiveEffectLike(record: EffectRecord): ReactiveEffectLike {
    return {
        deps: [],                   // not used by hook.ts — deps managed internally
        active: isActive(record.flags),
        fn: record.fn,
        scheduler: record.scheduler,
        run() { runEffectV2(record) },
        stop() { stopEffectV2(record) },
        cleanup() { /* deps cleaned internally in runEffectV2 */ },
    } as unknown as ReactiveEffectLike
}

/**
 * Create and immediately run a V2 reactive effect.
 *
 * When `attachToComponent` is true (default), the effect is registered with
 * the current component instance and stopped on unmount.
 *
 * The returned runner has `.effect` property (same shape as V1 runner) for
 * compatibility with code that accesses runner.effect.stop().
 */
export function createEffectV2(
    fn: () => void,
    options: { scheduler?: () => void; attachToComponent?: boolean } = {}
): ((...args: unknown[]) => void) & { effect: ReactiveEffectLike } {
    const record = makeEffectRecord(fn, options.scheduler)

    // Run immediately
    runEffectV2(record)

    const { attachToComponent = true } = options
    if (attachToComponent) {
        const component = getComponent()
        if (component) {
            const asLike = wrapAsReactiveEffectLike(record)
            registerEffectWithComponent(component, asLike)
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const runner: any = () => runEffectV2(record)
    runner.effect = wrapAsReactiveEffectLike(record)
    return runner
}
